/**
 * AI Gateway — catálogo público de modelos.
 *
 * Tipo y helpers compartidos entre el tabulador (/ai/precios) y las fichas
 * por modelo (/ai/modelos/:vendor/:model). Los datos SIEMPRE vienen del
 * backend (`/api/ai/gateway/pricing*`), que ya aplica el markup: aquí no se
 * calcula ningún precio, solo formato.
 */

import { fmtCredits } from '@/components/ai-gateway/format';

export interface PricingModel {
	id: string;
	name: string;
	description?: string;
	created?: number;
	context_length: number;
	capabilities: string[];
	architecture?: {
		input_modalities?: string[];
		output_modalities?: string[];
		tokenizer?: string;
		instruct_type?: string | null;
	};
	top_provider?: {
		context_length?: number;
		max_completion_tokens?: number | null;
		is_moderated?: boolean;
	};
	knowledge_cutoff?: string | null;
	reasoning?: { enabled?: boolean } | null;
	supported_parameters?: string[];
	pricing_usd_per_1m: {
		input: number;
		output: number;
		input_cache_read?: number;
		input_cache_write?: number;
		web_search?: number;
		image?: number;
		request?: number;
	};
}

export interface ModelDetailResponse {
	model: PricingModel;
	variants: PricingModel[];
	related: PricingModel[];
	count: number;
	updated_at: string;
}

export const CAPABILITY_LABELS: Record<string, Record<string, string>> = {
	es: {
		text: 'Texto / LLM',
		vision: 'Visión / OCR',
		image: 'Imagen',
		audio: 'Audio',
		voice: 'Voz',
		video: 'Video',
		embeddings: 'Embeddings',
		rerank: 'Rerank',
		tools: 'Tools',
		structured_outputs: 'JSON',
		reasoning: 'Reasoning',
	},
	en: {
		text: 'Text / LLM',
		vision: 'Vision / OCR',
		image: 'Image',
		audio: 'Audio',
		voice: 'Voice',
		video: 'Video',
		embeddings: 'Embeddings',
		rerank: 'Rerank',
		tools: 'Tools',
		structured_outputs: 'JSON',
		reasoning: 'Reasoning',
	},
};

export const CAPABILITY_ORDER = [
	'text',
	'vision',
	'image',
	'audio',
	'voice',
	'video',
	'embeddings',
	'rerank',
	'tools',
	'structured_outputs',
	'reasoning',
];

// 100 créditos = $1 USD. Mensaje típico: 1k tokens de input + 500 de output.
export function messagesPer100Credits(input: number, output: number): string {
	const costUsd = (1000 * input + 500 * output) / 1_000_000;
	if (costUsd <= 0) return '—';
	const n = 1 / costUsd;
	if (n > 10_000) return '10k+';
	const rounded =
		n >= 1000 ? Math.round(n / 100) * 100 : n >= 100 ? Math.round(n / 10) * 10 : Math.round(n);
	return `≈ ${rounded.toLocaleString('en-US')}`;
}

// USD por 1M tokens (API) → créditos por 1M tokens (UI). 1 crédito = $0.01 USD.
export const creditsPer1M = (usd: number): string => fmtCredits(usd * 100);

/** Vendor legible a partir del id `vendor/model`. */
export const modelVendor = (id: string): string => id.split('/')[0] || '';

/** Ruta interna de la ficha de un modelo según locale. */
export function modelDetailPath(locale: string, id: string): string {
	return locale === 'en' ? `/en/ai/models/${id}` : `/ai/modelos/${id}`;
}

/**
 * URLs canónicas públicas de la ficha (para Helmet/hreflang y sitemap).
 * Con slash final: Cloudflare Pages sirve la página pre-renderizada ahí y
 * responde 308 desde la variante sin slash, así el canonical no apunta a un
 * redirect.
 */
export function modelCanonicalUrls(id: string): { es: string; en: string } {
	return {
		es: `https://deployalo.com/ai/modelos/${id}/`,
		en: `https://deployalo.com/en/ai/models/${id}/`,
	};
}
